import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import moment from 'moment';

import { CustomNotification, dialogFunction } from 'common/components';

import { useGetMemberId } from 'hooks';
import {
  useGetPendingWizardForm,
  useGetWizardForm,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useGetWizardForm';
import { wizardFormKeys } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/queryKeys';
import { useCancelPendingFormWizard } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useCancelPendingFormWizard';

import { MODAL_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepModalConstants';
import {
  transformWizardItems,
  autoPopulateCaseGtin,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

import { buildWizardStep } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardStep';

import { sleep } from 'utils/delay';
import { addUtcForIsoString } from 'utils/timezone';
import { applyValidationResult } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';

export const usePendingWizardForm = ({
  enabled,
  actions,
  onClose,
  onValidate,
}) => {
  const queryClient = useQueryClient();

  const [showWizardContent, setShowWizardContent] = useState(false);

  const { memberId } = useGetMemberId();

  // Fetch pending form
  const {
    data: pendingForm,
    isLoading: isPendingLoading,
    fetchPendingWizard,
  } = useGetPendingWizardForm({
    enabled,
  });

  const wizardFormId = pendingForm?.data?.wizardFormId;

  // Fetch full wizard form data
  const { refetch: fetchWizardForm } = useGetWizardForm(
    { wizardFormId },
    { enabled: false }
  );

  // Cancel pending form mutation
  const { mutate: cancelPendingForm, isLoading: isCanceling } =
    useCancelPendingFormWizard();

  /**
   * Check for pending form - returns boolean, does NOT open modal
   * Let the caller decide what to do with the result
   */
  const checkPendingForm = useCallback(async () => {
    const pendingWizardResponse = await fetchPendingWizard();
    const hasPendingForm = !!pendingWizardResponse?.data?.wizardFormId;

    if (!hasPendingForm) {
      setShowWizardContent(true);
    }

    // Return the result so caller can decide what to do
    return hasPendingForm;
  }, [fetchPendingWizard]);

  /**
   * Reset state when wizard closes
   */
  const reset = useCallback(() => {
    setShowWizardContent(false);
  }, []);

  /**
   * Continue with pending form - loads data and validates
   */
  const handleContinue = useCallback(async () => {
    if (!wizardFormId) {
      actions.closeModal();
      setShowWizardContent(true);
      return;
    }

    actions.setWizardFormId(wizardFormId);

    try {
      const { data: formData } = await fetchWizardForm();

      if (formData?.data) {
        const data = formData.data;
        let updatedProducts = transformWizardItems(data.wizardFormItems || []).map(autoPopulateCaseGtin);

        const restoredState = buildWizardStep(data);

        const validateResponse = await onValidate({
          products: updatedProducts,
          memberId,
          showMessage: false,
        });

        updatedProducts = applyValidationResult({
          response: validateResponse,
          products: updatedProducts,
        });

        actions.setProducts(updatedProducts);
        actions.restoreState({
          ...restoredState,
          products: updatedProducts,
        });

        // Set answers
        Object.entries(data).forEach(([field, value]) => {
          if (field !== 'wizardFormItems') {
            actions.setAnswer(field, value);
          }
        });

        // Set arrival date
        if (data.arrivalDate) {
          let mappingArriveDate = addUtcForIsoString(data.arrivalDate);
          mappingArriveDate = mappingArriveDate
            ? moment(mappingArriveDate)
            : null;

          actions.setDate(mappingArriveDate);
        }
      }
    } catch (error) {
      CustomNotification.error('Failed to load pending form');
    } finally {
      actions.closeModal();
      await sleep(300);
      actions.openModal(MODAL_TYPES.ONLINE_GRID);
      setShowWizardContent(true);
    }
  }, [wizardFormId, actions, fetchWizardForm, onValidate, memberId]);

  /**
   * Cancel/delete the pending form
   */
  const handleCancelForm = useCallback(() => {
    if (!wizardFormId) {
      actions.reset();
      actions.closeModal();
      setShowWizardContent(true);
      return;
    }

    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to cancel the pending form?',
      width: 500,
      onOk: () => {
        cancelPendingForm(wizardFormId, {
          onSuccess: (response) => {
            if (response?.isSuccess) {
              CustomNotification.success('Pending form cancelled successfully');

              // Clear cache
              queryClient.setQueryData(wizardFormKeys.pending(), null);
              queryClient.invalidateQueries({
                queryKey: wizardFormKeys.pending(),
              });

              actions.closeModal();
            }
          },
          onError: () => {
            CustomNotification.error('Failed to cancel pending form');
          },
        });
      },
    });
  }, [wizardFormId, actions, cancelPendingForm, queryClient]);

  /**
   * Start a new form (cancels existing pending form)
   */
  const handleNewForm = useCallback(() => {
    if (!wizardFormId) {
      actions.reset();
      actions.closeModal();
      setShowWizardContent(true);
      return;
    }

    dialogFunction({
      type: 'warn',
      content:
        'Starting a new form will delete your previous submission. Are you sure you want to continue?',
      okText: 'Continue',
      cancelText: 'Cancel',
      width: 500,
      onOk: () => {
        cancelPendingForm(wizardFormId, {
          onSuccess: (response) => {
            if (response?.isSuccess) {
              // Clear cache
              queryClient.setQueryData(wizardFormKeys.pending(), null);
              queryClient.invalidateQueries({
                queryKey: wizardFormKeys.pending(),
              });

              actions.reset();
              actions.closeModal();
              setShowWizardContent(true);
            }
          },
          onError: () => {
            CustomNotification.error('Failed to start new form');
          },
        });
      },
    });
  }, [wizardFormId, actions, cancelPendingForm, queryClient]);

  /**
   * Cancel pending form without showing confirmation dialog
   * Returns a promise - use this when you want to handle the flow yourself
   */
  const cancelPendingFormAsync = useCallback(async () => {
    if (!wizardFormId) {
      actions.reset();
      actions.closeModal();
      setShowWizardContent(true);
      return { isSuccess: true };
    }

    return new Promise((resolve) => {
      cancelPendingForm(wizardFormId, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            // Clear cache
            queryClient.setQueryData(wizardFormKeys.pending(), null);
            queryClient.invalidateQueries({
              queryKey: wizardFormKeys.pending(),
            });

            actions.reset();
            actions.closeModal();
            setShowWizardContent(true);
          }
          resolve(response);
        },
        onError: (error) => {
          CustomNotification.error('Failed to cancel pending form');
          resolve({ isSuccess: false, error });
        },
      });
    });
  }, [wizardFormId, actions, cancelPendingForm, queryClient]);

  /**
   * Close the pending modal and wizard
   */
  const handleClose = useCallback(() => {
    actions.closeModal();
    setShowWizardContent(false);
    onClose?.();
  }, [actions, onClose]);

  return {
    // State
    showWizardContent,
    isPendingLoading,
    isCanceling,
    hasPendingForm: Boolean(wizardFormId),
    wizardFormId,

    // Actions
    checkPendingForm,
    reset,
    handleContinue,
    handleCancelForm,
    handleNewForm,
    handleClose,
    fetchPendingWizard,
    cancelPendingFormAsync,
  };
};

export default usePendingWizardForm;
